<script setup>
import { useCotizacionParametros } from "@/composables/useCotizacionParametros";
import {
  useCotizacionUnidadMargen,
  useCotizacionUnidadMargenResidual,
} from "@/composables/useCotizacionUnidad";
import { ref, onMounted, computed, watch } from "vue";
import useClientes from "@/composables/useClientes";
import useUtil from "@/composables/useUtil";
import LoadingSpinner from "@/components/LoadingComponent.vue";
import Slider from "vue3-slider";
import ArgonAlertVue from "@/components/ArgonAlert.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import appApi from "@/services/api";
import { exceptionRequestMsg, showToast } from "@/assets/js/util";
import { useRoute, useRouter } from "vue-router";
import ArgonAlert from "@/components/ArgonAlert.vue";
const router = useRouter();
import CurrencyInput from "@/components/CurrencyInput.vue";

const { formatoMoneda, obtenerFechaActual, confirmAction } = useUtil();
const { listaParametros, get: getParametros } = useCotizacionParametros();
const {
  get: getCotizacionUnidadMargenResidual,
  listaMargenes: listaMargenesResiduales,
} = useCotizacionUnidadMargenResidual();
const {
  get: getCotizacionUnidadMargen,
  listaMargenes,
} = useCotizacionUnidadMargen();
const { getClientes } = useClientes();
const clientes = ref([]);
const periodicidades = [
  "Mensual",
  "Bimestral",
  "Trimestral",
  "Semestral",
  "Anual",
];
const tasaMinima = {
  maquinaria: 52,
  transporte: 45,
};
const formCotizacion = ref(null);
const mostrarCampos = ref(false);
const tratamientoAnticipo = ["Renta extraordinaria", "Residual"];
const plazosAnios = [1, 2, 3, 4, 5];
const formasDeCobro = ["Vencido", "Anticipado"];
const originadores = ["Marketing", "Externo", "Promotor"]; // TODO: Tomarlas desde BD con todo y comisión
const guardando = ref(false);

const state = ref({
  valor: 1000000,
  anticipo: 15,
  cliente: null,
  atencion: null,
  capital: null,
  descripcion: null,
  plazoAnios: 4,
  periodicidad: "Mensual",
  rentas: 0,
  arrendamientoTipo: 14, // MAQUINARIA
  tasa: 23,
  residual: 15,
  comision: 3,
  formasDeCobro: "Anticipado",
  tratamientoAnticipo: "Renta extraordinaria",
  fondeo: 20,
  tiie: 7.71,
  comisionEjecutivo: 8.5,
  comisionRegional: 2,
  originador: "Marketing",
  seguro: 0,
  seguroFinanciado: true,
  accesorios: 0,
  flujoCliente: 1,
  comisionAgencia: 0,
  comisionExterno: 0,
});

const cumpleTasaMinima = computed(() => {
  if (
    localStorage.getItem("_id") === "9" ||
    localStorage.getItem("_id") === "138"
  )
    return true;

  if (
    state.value.arrendamientoTipo == 14 &&
    state.value.tasa >= tasaMinima.maquinaria
  )
    return true;
  if (
    state.value.arrendamientoTipo == 1 &&
    state.value.tasa >= tasaMinima.transporte
  )
    return true;

  return false;
});

const listaCalculosPlazo = ref([]);

const capital = computed(() => {
  if (!state.value.valor || isNaN(state.value.valor)) return 0;
  return parseFloat(state.value.valor) / 1.16;
});

const tasa = computed(() => {
  if (!state.value.tasa || isNaN(state.value.tasa)) return 0;
  return parseFloat(state.value.tasa) / 100;
});

const cantidadPagosPorAnio = computed(() => {
  if (state.value.periodicidad == "Mensual") return 12;
  if (state.value.periodicidad == "Bimestral") return 6;
  if (state.value.periodicidad == "Trimestral") return 4;
  if (state.value.periodicidad == "Semestral") return 2;
  if (state.value.periodicidad == "Anual") return 1;
});

const tasaPorPeriodo = computed(() => {
  if (state.value.periodicidad == "Mensual") return state.value.tasa / 12;
  if (state.value.periodicidad == "Bimestral") return state.value.tasa / 6;
  if (state.value.periodicidad == "Trimestral") return state.value.tasa / 4;
  if (state.value.periodicidad == "Semestral") return state.value.tasa / 2;
  if (state.value.periodicidad == "Anual") return state.value.tasa / 1;
});

const cantidadPagos = computed(() =>
  parseInt(state.value.plazoAnios * cantidadPagosPorAnio.value),
);

const anticipo = computed(() => {
  if (!state.value.anticipo || isNaN(state.value.anticipo)) return 0;
  return parseFloat(state.value.anticipo / 100);
});

const anticipoTotal = computed(() => {
  return parseFloat(capital.value * anticipo.value);
});

const seguro = computed(() => {
  if (!state.value.seguro || isNaN(state.value.seguro)) return 0;
  if (state.value.seguro == 0) return 0;

  return state.value.seguro / 1.16;
});

const seguroRenta = computed(() => {
  if (isNaN(seguro.value)) return 0;
  return state.value.seguroFinanciado ? seguro.value : 0;
});

const seguroAnticipo = computed(() => {
  if (isNaN(seguro.value)) return 0;
  return state.value.seguroFinanciado ? 0 : seguro.value;
});

const accesorios = computed(() => {
  if (!state.value.accesorios || isNaN(state.value.accesorios)) return 0;
  if (state.value.accesorios == 0) return 0;

  return state.value.accesorios / 1.16;
});

const montoFinanciado = computed(() => {
  return (
    parseFloat(capital.value - anticipoTotal.value) +
    accesorios.value +
    seguroRenta.value
  );
  //return parseFloat(capital.value) + accesorios.value + seguroRenta.value;
});

function calcularPago(rate, nper, pv, fv = 0, tipo = 0) {
  if (rate === 0) return -(pv + -residualTotal.value) / nper;

  const r = rate / 100;
  const factor = Math.pow(1 + r, nper);

  return parseFloat(
    -(
      ((r * (pv * factor + -residualTotal.value)) / (factor - 1)) *
      (tipo === 1 ? 1 / (1 + r) : 1)
    ),
  );
}

const renta = computed(() =>
  calcularPago(
    tasaPorPeriodo.value,
    cantidadPagos.value,
    montoFinanciado.value,
  ),
);

const rentasEnDeposito = computed(() => {
  if (!state.value.rentas || isNaN(state.value.rentas)) return 0;
  return parseFloat(-renta.value * state.value.rentas);
});

const residualTotal = computed(
  () => parseFloat((state.value.residual / 100) * capital.value),

  /* parseFloat(
    (state.value.residual / 100) *
      (capital.value + accesorios.value + seguroRenta.value),
  ), */
);

const comision = computed(() => {
  if (
    !state.value.comision ||
    isNaN(state.value.comision) ||
    !state.value.valor ||
    isNaN(state.value.valor)
  )
    return 0;
  //return parseFloat(capital.value * (state.value.comision / 100));
  return parseFloat(state.value.valor * (state.value.comision / 100));
});

const diasPorPeriodicidad = computed(() => {
  switch (state.value.periodicidad) {
    case "Mensual":
      return 30;
    case "Bimestral":
      return 60;
    case "Trimestral":
      return 90;
    case "Semestral":
      return 180;
    case "Anual":
      return 360;
    default:
      return null;
  }
});

const rentaPositiva = computed(() => -renta.value);
const costoDiarioArrendamiento = computed(
  () => rentaPositiva.value / diasPorPeriodicidad.value,
);

const descuentosIniciales = computed(() => {
  return (
    rentasEnDeposito.value +
    comision.value +
    anticipoTotal.value +
    seguroAnticipo.value
  );
}); // TODO: Revisar la fórmula con el valor final de los descuentos en Excel

const residualFinal = computed(() => {
  return state.value.tratamientoAnticipo == "Residual"
    ? residualTotal.value - anticipoTotal.value
    : residualTotal.value;
});

const calculaRentaPorPeriodo = (numeroPlazo) => {
  if (numeroPlazo == cantidadPagos.value + 1) {
    return residualFinal.value + rentaPositiva.value;
  }

  return rentaPositiva.value;
};

const flujos = computed(() => {
  const flujos = [];

  // Periodo 0
  if (state.value.formasDeCobro === "Vencido") {
    flujos.push(-(capital.value - descuentosIniciales.value));
  } else {
    // Solo si el primer pago es inmediato al inicio (por ejemplo, arrendamiento anticipado)
    flujos.push(
      -capital.value + (descuentosIniciales.value + rentaPositiva.value),
    ); // NO sumes la renta
  }

  // Pagos periódicos (empezamos desde 1)
  for (let i = 1; i <= cantidadPagos.value; i++) {
    flujos.push(calculaRentaPorPeriodo(i));
  }

  return flujos;
});

function calcularTIR(estimacionInicial = 0.001) {
  const maxIteraciones = 1000;
  const tolerancia = 1e-7;
  let tasa = estimacionInicial;

  // Validación inicial
  if (!Array.isArray(flujos.value) || flujos.value.length === 0) {
    throw new Error("El arreglo de flujos está vacío o no es válido.");
  }

  const tieneNegativos = flujos.value.some((f) => f < 0);
  const tienePositivos = flujos.value.some((f) => f > 0);

  if (!tieneNegativos || !tienePositivos) {
    throw new Error(
      "Se necesita al menos un flujo negativo y uno positivo para calcular la TIR.",
    );
  }

  for (let i = 0; i < maxIteraciones; i++) {
    let valorPresente = 0;
    let derivada = 0;

    for (let j = 0; j < flujos.value.length; j++) {
      const denominador = Math.pow(1 + tasa, j);
      valorPresente += flujos.value[j] / denominador;
      derivada -= (j * flujos.value[j]) / (denominador * (1 + tasa));
    }

    // Validar que la derivada no es 0 (evita división por cero)
    if (derivada === 0) {
      throw new Error(
        "La derivada es cero. No se puede continuar con el método de Newton-Raphson.",
      );
    }

    const nuevaTasa = tasa - valorPresente / derivada;

    // Validar que la tasa es un número válido
    if (!isFinite(nuevaTasa)) {
      throw new Error("La tasa calculada no es un número válido.");
    }

    if (Math.abs(nuevaTasa - tasa) < tolerancia) {
      return tasa * 100;
    }

    tasa = nuevaTasa;
  }

  throw new Error(
    "No se pudo calcular la TIR: se excedió el número máximo de iteraciones.",
  );
}

const TIR = computed(() => calcularTIR());

const TIRporPeriodo = computed(() => {
  switch (state.value.periodicidad) {
    case "Mensual":
      return TIR.value * 12;
    case "Bimestral":
      return TIR.value * 6;
    case "Trimestral":
      return TIR.value * 4;
    case "Semestral":
      return TIR.value * 2;
    case "Anual":
      return TIR.value * 1;
    default:
      return 0;
  }
});

const tasaPeriodica = computed(() => {
  switch (state.value.periodicidad) {
    case "Mensual":
      return state.value.fondeo / 12; // mensual
    case "Bimestral":
      return state.value.fondeo / 6; // bimestral
    case "Trimestral":
      return state.value.fondeo / 4; // trimestral
    case "Semestral":
      return state.value.fondeo / 2; // semestral
    case "Anual":
      return state.value.fondeo / 1; // anual
    default:
      throw new Error("Periodicidad no válida");
  }
});

function calcularVNA(tasa, flujos, periodoInicial = 1) {
  return flujos.reduce((acumulado, flujo, i) => {
    const periodo = i + periodoInicial;
    return acumulado + flujo / Math.pow(1 + tasa, periodo);
  }, 0);
}

// Cálculo según condición
const margenCantidad = computed(() => {
  const tasa = tasaPeriodica.value;
  const flujosActuales = flujos.value;
  const tasaDecimal = tasa / 100;
  //const restaComision =
  //state.value.arrendamientoTipo == 14
  //? comisionBroker.value
  //: comisionAgencia.value;
  const restaComision = comisionBroker.value + comisionAgencia.value;
  if (state.value.formasDeCobro === "Anticipado") {
    const flujosDesdeT2 = flujosActuales.slice(2);
    const flujoT1 = Math.abs(
      -capital.value + (descuentosIniciales.value + rentaPositiva.value),
    );
    const VNA = calcularVNA(tasaDecimal, flujosDesdeT2, 1) - flujoT1;
    return VNA - restaComision;
  } else {
    const flujoT0 = flujosActuales[0];
    const flujosDesdeT1 = flujosActuales.slice(1);
    const VNA = calcularVNA(tasaDecimal, flujosDesdeT1, 1) + flujoT0;
    return VNA - restaComision;
  }
});

const margenPorcentaje = computed(
  () => margenCantidad.value / montoFinanciado.value,
);

const spreed = computed(() => state.value.tasa - state.value.tiie);

const parametros = computed(() => {
  if (listaParametros.value.length == 0) return undefined;
  return listaParametros.value.filter(
    (parametro) =>
      parametro.arrendamiento_tipo_id == state.value.arrendamientoTipo,
  )[0];
});

const comisionEjecutivo = computed(
  () => margenCantidad.value * (parametros.value.comision_ejecutivo / 100),
);

const comisionLider = computed(
  () => margenCantidad.value * (parametros.value.comision_lider / 100),
);

const comisionBroker = computed(
  () => montoFinanciado.value * (state.value.comisionExterno / 100),
);

const comisionMarketing = computed(
  () => margenCantidad.value * (parametros.value.comision_marketing / 100),
);

const comisionAgencia = computed(
  () => montoFinanciado.value * (state.value.comisionAgencia / 100),
);

const leyendaFlujoCliente = (minimo, maximo) => {
  if (minimo === null) return "Hasta $" + formatoMoneda(maximo);
  if (maximo === null) return "$" + formatoMoneda(minimo) + " o más";
  return `Entre $${formatoMoneda(minimo)} y $${formatoMoneda(maximo)}`;
};

const flujoClienteSeleccionado = computed(() => {
  if (!listaMargenes.value) return undefined;
  return listaMargenes.value.filter(
    (flujo) => flujo.id === state.value.flujoCliente,
  );
});

const porcentajeMargenMinimoPorPlazo = computed(() => {
  if (!flujoClienteSeleccionado.value) return undefined;
  return flujoClienteSeleccionado.value?.[0]?.[`plazo_${cantidadPagos.value}`];
});

const porcentajeMargenMinimoPorPlazoEnRango = computed(() => {
  if (!porcentajeMargenMinimoPorPlazo.value) return false;
  if (
    localStorage.getItem("_id") === "9" ||
    localStorage.getItem("_id") === "138"
  )
    return true;
  if (
    parseFloat(margenPorcentaje.value * 100) >=
    parseFloat(porcentajeMargenMinimoPorPlazo.value)
  )
    return true;

  return false;
});

const mostrarAlertasPlazo = computed(() => {
  if (!porcentajeMargenMinimoPorPlazo.value) return true;
  if (
    parseFloat(margenPorcentaje.value * 100) >=
    parseFloat(porcentajeMargenMinimoPorPlazo.value)
  )
    return false;

  return true;
});

const rangoTIR = computed(() => {
  if (
    parseFloat(margenPorcentaje.value * 100) <
    parseFloat(porcentajeMargenMinimoPorPlazo.value)
  )
    return "Menor";

  return "En rango";
});

const rangosMargenResidual = computed(() => {
  if (!listaMargenesResiduales.value)
    return {
      minimo: 0,
      maximo: 10,
    };

  const margenResidualMinimo = listaMargenesResiduales.value.filter(
    (el) => el.plazo == cantidadPagos.value && el.tipo == "minimo",
  );

  const margenResidualMaximo = listaMargenesResiduales.value.filter(
    (el) => el.plazo == cantidadPagos.value && el.tipo == "maximo",
  );
  return {
    minimo: parseInt(margenResidualMinimo[0].porcentaje),
    maximo: parseInt(margenResidualMaximo[0].porcentaje),
  };
});

const agregarPlazoCalculo = () => {
  if (listaCalculosPlazo.value.length === 5) return; // No hay más plazos por guardar
  /* if (
    existenPlazosGuardados.value &&
    state.value.residual > listaCalculosPlazo.value[0].porcentajeResidual
  )
    return showToast(
      `El valor residual no coincide con el capturado en el plazo anterior (${listaCalculosPlazo.value[0].porcentajeResidual}%)`,
      "warning"
    ); */
  if (listaCalculosPlazo.value.length === 0)
    return agregaInfoPlazoCalculoIndividual();
  const existePlazo = listaCalculosPlazo.value.some(
    (item) => item.plazo === cantidadPagos.value,
  );
  if (existePlazo) {
    alert(
      `El plazo ${cantidadPagos.value} ya existe. Elimine el plazo para agregarlo de nuevo`,
    );
    return;
  }
  agregaInfoPlazoCalculoIndividual();
};

const seguroMensual = computed(() => {
  if (!state.value.seguroFinanciado) return 0;
  const i = state.value.tasa / 100 / 12;
  return (seguro.value * i) / (1 - Math.pow(1 + i, -cantidadPagos.value));
});

const agregaInfoPlazoCalculoIndividual = () => {
  listaCalculosPlazo.value.push({
    plazo: cantidadPagos.value,
    renta: renta.value,
    rentaDeposito: rentasEnDeposito.value,
    residual: residualFinal.value,
    residualPorcentaje: state.value.residual,
    tir: TIR.value,
    tirPorPeriodo: TIRporPeriodo.value,
    porcentajeMargenMinimoPorPlazo: porcentajeMargenMinimoPorPlazo.value,
    margenCantidad: margenCantidad.value,
    margenPorcentaje: margenPorcentaje.value,
    comisionMarketing: comisionMarketing.value,
    comisionEjecutivo: comisionEjecutivo.value,
    comisionLider: comisionLider.value,
    comisionBroker: comisionBroker.value,
    comisionAgencia: comisionAgencia.value,
    porcentajeComisionMarketing: parametros.value.comision_marketing,
    porcentajeComisionEjecutivo: parametros.value.comision_ejecutivo,
    porcentajeComisionLider: parametros.value.comision_lider,
    porcentajeComisionBroker: state.value.comisionExterno,
    porcentajeComisionAgencia: state.value.comisionAgencia,
    residualMinimo: rangosMargenResidual.value.minimo,
    residualMaximo: rangosMargenResidual.value.maximo,
    seguroMensual: seguroMensual.value,
  });
};

const eliminarPlazo = (plazo) => {
  try {
    listaCalculosPlazo.value = listaCalculosPlazo.value.filter(
      (el) => el.plazo != plazo,
    );
  } catch (error) {
    console.error(error);
  }
};

const existenPlazosGuardados = computed(() =>
  listaCalculosPlazo.value.length > 0 ? true : false,
);

const confirmaGuardado = async () => {
  const confirmado = await confirmAction({
    title: "¿Guardar cotización?",
  });

  if (confirmado) {
    await guardarCotizacion();
  }
};

const guardarCotizacion = async () => {
  guardando.value = true;
  try {
    const response = await appApi().post("cotizaciones/unidad/store", {
      ...state.value,
      detalle: listaCalculosPlazo.value,
      arrendamiento: parametros.value.arrendamiento,
      capital: capital.value,
      spreed: spreed.value,
      seguroFinanciado: seguro.value == 0 ? 0 : state.value.seguroFinanciado,
      anticipo: anticipoTotal.value,
      porcentajeAnticipo: state.value.anticipo,
      descuentosIniciales: descuentosIniciales.value,
      tasaPeriodica: tasaPeriodica.value,
      flujoCliente: flujoClienteSeleccionado.value[0].tipo_cliente,
      montoFinanciado: montoFinanciado.value,
      comision: comision.value,
      porcentajeComision: state.value.comision,
    });

    router.push({
      name: "cotizacion-unidades-formato",
      params: {
        token: response.data.cotizacion.token,
      },
    });
    showToast(response.data.message, "success");
  } catch (error) {
    console.error(error);
    exceptionRequestMsg(error);
  } finally {
    guardando.value = false;
  }
};

watch(
  () => state.value.plazoAnios,
  () => {
    state.value.residual = rangosMargenResidual.value.minimo + 5;
  },
);

onMounted(async () => {
  clientes.value = await getClientes();
  await getParametros();
  await getCotizacionUnidadMargen();
  await getCotizacionUnidadMargenResidual();

  console.log(cantidadPagos.value);

  watch(
    () => state.value.arrendamientoTipo,
    (nuevoValor) => {
      state.value.tasa =
        nuevoValor === 14 ? tasaMinima.maquinaria : tasaMinima.transporte;
      state.value.comisionAgencia = parametros.value.comision_agencia;
      state.value.comisionExterno = parametros.value.comision_externo;
    },
    { immediate: true },
  );

  watch(
    () => state.value.valor,
    (nuevoValor, viejoValor) => {
      if (isNaN(nuevoValor) || nuevoValor <= 0) {
        state.value.valor = viejoValor;
      }
    },
    { immediate: true },
  );
});
</script>

<template>
  <LoadingSpinner v-if="clientes.length == 0 || !listaParametros" />
  <div class="py-4 container-fluid" v-else>
    <div class="row">
      <div class="col-12">
        <form
          ref="formCotizacion"
          @submit.prevent="confirmaGuardado"
          class="card"
        >
          <div class="card-header pb-0 border-bottom">
            <div class="d-flex justify-content-center align-items-center">
              <div
                class="col-md-12 d-flex flex-row justify-content-between align-items-center pb-3"
              >
                <h6>Nueva cotización</h6>
                <div class="d-flex gap-2">
                  <ArgonButton
                    size="sm"
                    color="light"
                    type="button"
                    @click="agregarPlazoCalculo"
                    v-if="porcentajeMargenMinimoPorPlazoEnRango"
                  >
                    Añadir plazo
                  </ArgonButton>
                  <!-- <ArgonAlert color="warning" v-else>
                    No se cumple con la tasa mínima requerida. Favor de
                    verificar y recalcular.
                  </ArgonAlert> -->
                  <ArgonButton
                    size="sm"
                    color="primary"
                    v-if="listaCalculosPlazo.length > 0"
                    type="submit"
                    :loading="guardando"
                  >
                    Guardar
                  </ArgonButton>
                </div>
              </div>
            </div>
          </div>
          <div class="container row py-3">
            <div class="col-lg-12 row">
              <ArgonAlertVue
                v-if="mostrarAlertasPlazo"
                color="danger"
                icon="ri ri-error-warning-line"
              >
                La cotización se encuentra fuera del margen mínimo. Ajuste y
                vuelva a intentar.
              </ArgonAlertVue>
              <div class="py-3 row gap-3">
                <div
                  class="border-bottom p-3 col-md-4 rounded shadow col-md-4"
                  v-for="el in listaCalculosPlazo"
                  :key="el.plazo"
                >
                  <div>
                    <div
                      class="d-flex flex-row align-items-center justify-content-between"
                    >
                      <u
                        ><h6>{{ el.plazo }} meses</h6></u
                      >
                      <Argon-Button
                        type="button"
                        size="xs"
                        color="danger"
                        @click="eliminarPlazo(el.plazo)"
                        ><i class="ri-delete-bin-line"></i
                      ></Argon-Button>
                    </div>
                    <p>
                      <b>Renta:</b> ${{ formatoMoneda(el.renta, false) }}<br />
                      <b>Residual:</b> ${{
                        formatoMoneda(el.residual, false)
                      }}
                      ({{ formatoMoneda(el.residualPorcentaje, false) }}%)<br />
                      <b>TIR:</b> {{ formatoMoneda(el.tir, false) }}%
                    </p>
                  </div>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <tbody>
                    <tr>
                      <th colspan="2">Arrendamiento*</th>
                      <td colspan="2">
                        <select
                          name="arrendamiento"
                          id="arrendamiento"
                          v-model="state.arrendamientoTipo"
                          class="form-control form-control-sm"
                          :disabled="existenPlazosGuardados"
                        >
                          <option
                            :value="arrendamiento_tipo_id"
                            :key="arrendamiento_tipo_id"
                            v-for="{
                              arrendamiento_tipo_id,
                              arrendamiento,
                            } in listaParametros"
                          >
                            {{ arrendamiento }}
                          </option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Cliente*</th>
                      <td colspan="2">
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          id="cliente"
                          name="cliente"
                          v-model="state.cliente"
                          autocomplete="off"
                          placeholder="Ej: Juan Pérez"
                          required
                        />
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Atención a*</th>
                      <td colspan="2">
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          id="atencion"
                          name="atencion"
                          required
                          v-model="state.atencion"
                          autocomplete="off"
                          placeholder="Ej: Grupo Pérez Ganadero S.A. de C.V."
                        />
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Descripción*</th>
                      <td colspan="2">
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          id="descripcion"
                          name="descripcion"
                          required
                          v-model="state.descripcion"
                          autocomplete="off"
                          placeholder="Ej: Tesla Model X 2025"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Flujo cliente*</th>
                      <td colspan="2">
                        <select
                          name="flujo"
                          class="form-control form-control-sm"
                          id="flujo"
                          v-model="state.flujoCliente"
                          :disabled="existenPlazosGuardados"
                        >
                          <option
                            :value="id"
                            :key="id"
                            v-for="{ id, minimo, maximo } in listaMargenes"
                          >
                            {{ leyendaFlujoCliente(minimo, maximo) }}
                          </option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Valor de la unidad</th>
                      <td colspan="2">
                        <div class="input-group">
                          <span class="input-group-text"> $ </span>
                          <currency-input
                            class="form-control form-control-sm"
                            type="tel"
                            id="valor"
                            v-model="state.valor"
                            autocomplete="off"
                            :disabled="existenPlazosGuardados"
                            required
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Anticipo</th>
                      <td colspan="2">
                        <div class="input-group">
                          <input
                            class="form-control form-control-sm"
                            type="tel"
                            id="anticipo"
                            v-model="state.anticipo"
                            autocomplete="off"
                            :disabled="existenPlazosGuardados"
                            required
                          />
                          <span class="input-group-text">%</span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Capital</th>
                      <td colspan="2" class="bg-light py-2 text-center">
                        ${{ formatoMoneda(capital, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Plazo en años</th>
                      <td>
                        <select
                          class="form-control form-control-sm"
                          v-model="state.plazoAnios"
                          required
                        >
                          <option
                            :value="el"
                            v-for="el in plazosAnios"
                            :key="el"
                          >
                            {{ el }} años
                          </option>
                        </select>
                      </td>
                      <td class="bg-light py-2">{{ cantidadPagos }} meses</td>
                    </tr>
                    <tr>
                      <th colspan="2">Renta</th>
                      <td></td>
                      <td class="bg-light py-2">
                        ${{ formatoMoneda(renta, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Rentas en depósito</th>
                      <td>
                        <input
                          type="tel"
                          name="rentas"
                          id="rentas"
                          :disabled="existenPlazosGuardados"
                          v-model="state.rentas"
                          required
                          autocomplete="off"
                          class="form-control form-control-sm"
                        />
                      </td>
                      <td class="bg-light py-2">
                        ${{ formatoMoneda(rentasEnDeposito, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Seguro</th>
                      <td>
                        <div class="d-flex flex-row gap-1">
                          <div class="input-group">
                            <span class="input-group-text">$</span>
                            <CurrencyInput
                              :disabled="existenPlazosGuardados"
                              type="tel"
                              class="form-control form-control-sm"
                              name="seguro"
                              v-model="state.seguro"
                              required
                              autocomplete="off"
                            />
                          </div>
                          <select
                            v-show="seguro > 0 ? true : false"
                            name="seguroFinanciado"
                            :disabled="existenPlazosGuardados"
                            id="seguroFinanciado"
                            v-model="state.seguroFinanciado"
                            class="form-control form-control-sm"
                          >
                            <option :value="true">Financiado</option>
                            <option :value="false">Contado</option>
                          </select>
                        </div>
                      </td>
                      <td class="bg-light py-2">
                        ${{ formatoMoneda(seguro, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Accesorios</th>
                      <td>
                        <div class="input-group">
                          <span class="input-group-text">$</span>
                          <CurrencyInput
                            type="tel"
                            :disabled="existenPlazosGuardados"
                            class="form-control form-control-sm"
                            name="seguro"
                            v-model="state.accesorios"
                            required
                            autocomplete="off"
                          />
                        </div>
                      </td>
                      <td class="bg-light py-2">
                        ${{ formatoMoneda(accesorios, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Residual</th>
                      <td class="align-middle d-flex flex-column text-center">
                        <div>
                          <!-- <Slider
                            color="#f7bf00"
                            track-color="#EAEAEA"
                            :min="rangosMargenResidual.minimo"
                            :max="rangosMargenResidual.maximo"
                            :height="8"
                            v-model="state.residual"
                            :tooltip="true"
                            tooltipText="%v"
                          /> -->
                          <input
                            class="form-control form-control-sm"
                            v-model="state.residual"
                          />
                        </div>
                        <!-- {{ formatoMoneda(state.residual) }}% -->
                      </td>
                      <td class="bg-light py-2">
                        ${{ formatoMoneda(residualTotal, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Comisión</th>
                      <td>
                        <div class="input-group">
                          <input
                            type="tel"
                            class="form-control form-control-sm"
                            v-model="state.comision"
                            :disabled="existenPlazosGuardados"
                            name="comision"
                            required
                          />
                          <span class="input-group-text">%</span>
                        </div>
                      </td>
                      <td class="bg-light py-2">
                        ${{ formatoMoneda(comision, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th>Tasa</th>
                      <td>
                        <div class="input-group">
                          <input
                            type="tel"
                            class="form-control form-control-sm"
                            v-model="state.tasa"
                            :disabled="existenPlazosGuardados"
                            required
                          />
                          <span class="input-group-text">%</span>
                        </div>
                      </td>
                      <th class="text-center">Anticipo</th>
                      <td class="bg-light py-2">
                        ${{ formatoMoneda(anticipoTotal, false) }}
                      </td>
                    </tr>
                    <tr>
                      <th colspan="2">Descuentos iniciales</th>
                      <td colspan="2" class="bg-light py-2">
                        ${{ formatoMoneda(descuentosIniciales, false) }}
                      </td>
                    </tr>
                    <template v-if="mostrarCampos">
                      <tr>
                        <th colspan="2">Formas de cobro</th>
                        <td colspan="2">
                          <select
                            name="formasCobro"
                            id="formasCobro"
                            v-model="state.formasDeCobro"
                            class="form-control form-control-sm"
                          >
                            <option
                              :value="formaCobro"
                              v-for="formaCobro in formasDeCobro"
                              :key="formaCobro"
                            >
                              {{ formaCobro }}
                            </option>
                          </select>
                        </td>
                      </tr>
                      <tr>
                        <th colspan="2">Periodicidad</th>
                        <td colspan="2">
                          <select
                            name="periodicidad"
                            id="periodicidad"
                            v-model="state.periodicidad"
                            class="form-control form-control-sm"
                          >
                            <option
                              :value="el"
                              v-for="el in periodicidades"
                              :key="el"
                            >
                              {{ el }}
                            </option>
                          </select>
                        </td>
                      </tr>
                      <tr>
                        <th colspan="2">Tratamiento del anticipo</th>
                        <td colspan="2">
                          <select
                            name="tratamiento"
                            id="tratamiento"
                            class="form-control form-control-sm"
                            v-model="state.tratamientoAnticipo"
                          >
                            <option
                              :value="tratamiento"
                              v-for="tratamiento in tratamientoAnticipo"
                              :key="tratamiento"
                            >
                              {{ tratamiento }}
                            </option>
                          </select>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
              <hr />
              <div class="row">
                <div class="col-md-8">
                  <div class="table-responsive">
                    <table
                      class="table table-sm table-bordered table-striped table-hover"
                      id="tablaTotales"
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Precio</th>
                          <th>Pago</th>
                          <th>Flujo</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>0</td>
                          <td class="text-danger">
                            {{
                              state.formasDeCobro == "Vencido"
                                ? formatoMoneda(-capital, false)
                                : "-"
                            }}
                          </td>
                          <td>
                            {{
                              state.formasDeCobro == "Vencido"
                                ? "$" +
                                  formatoMoneda(descuentosIniciales, false)
                                : "-"
                            }}
                          </td>
                          <td>
                            {{
                              state.formasDeCobro == "Vencido"
                                ? "$" +
                                  formatoMoneda(
                                    capital - descuentosIniciales,
                                    false,
                                  )
                                : "-"
                            }}
                          </td>
                        </tr>
                        <tr>
                          <td>1</td>
                          <td class="text-danger">
                            {{
                              state.formasDeCobro != "Vencido"
                                ? "$" + formatoMoneda(-capital, false)
                                : "-"
                            }}
                          </td>
                          <td>
                            {{
                              state.formasDeCobro != "Vencido"
                                ? "$" +
                                  formatoMoneda(
                                    descuentosIniciales + rentaPositiva,
                                    false,
                                  )
                                : "$" + formatoMoneda(rentaPositiva)
                            }}
                          </td>
                          <td>
                            {{
                              state.formasDeCobro != "Vencido"
                                ? "$" +
                                  formatoMoneda(
                                    Math.abs(
                                      -capital +
                                        (descuentosIniciales + rentaPositiva),
                                    ),
                                    false,
                                  )
                                : "$" + formatoMoneda(rentaPositiva, false)
                            }}
                          </td>
                        </tr>
                        <tr v-for="index in cantidadPagos" :key="index">
                          <td>{{ index + 1 }}</td>
                          <td></td>
                          <td>
                            ${{
                              formatoMoneda(
                                calculaRentaPorPeriodo(index + 1),
                                false,
                              )
                            }}
                          </td>
                          <td>
                            ${{
                              formatoMoneda(
                                calculaRentaPorPeriodo(index + 1),
                                false,
                              )
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="table-responsive text-center">
                    <table class="table table-bordered table-sm">
                      <tr class="bg-light text-dark">
                        <th colspan="2">TIR</th>
                      </tr>
                      <tr>
                        <td class="bg-light">
                          {{ formatoMoneda(TIR, false) }}%
                        </td>
                        <td
                          class="text-white"
                          :class="
                            rangoTIR == 'Menor' ? 'bg-danger' : 'bg-success'
                          "
                        >
                          {{ formatoMoneda(TIRporPeriodo, false) }}%
                        </td>
                      </tr>
                    </table>
                    <table class="table table-bordered table-sm mt-4">
                      <tr>
                        <td>Monto financiado</td>
                        <td>${{ formatoMoneda(montoFinanciado, false) }}</td>
                      </tr>
                      <tr>
                        <td>Margen mínimo</td>
                        <td>
                          {{
                            formatoMoneda(
                              porcentajeMargenMinimoPorPlazo,
                              false,
                            )
                          }}%
                        </td>
                      </tr>
                      <tr>
                        <td>Margen ($)</td>
                        <td>${{ formatoMoneda(margenCantidad, false) }}</td>
                      </tr>
                      <tr
                        :class="
                          mostrarAlertasPlazo ? 'bg-danger text-white' : ''
                        "
                        class="mostrarAlertasPlazo ? 'bg-danger text-white' : ''"
                      >
                        <td>Margen (%)</td>
                        <td>
                          {{ formatoMoneda(margenPorcentaje * 100, false) }}%
                        </td>
                      </tr>
                      <tr>
                        <td>Tasa periódica</td>
                        <td>{{ formatoMoneda(tasaPeriodica, false) }}%</td>
                      </tr>
                    </table>
                    <table class="table table-bordered table-sm mt-4">
                      <tr>
                        <th>Fondeo</th>
                        <td>{{ formatoMoneda(parametros.fondeo, false) }}%</td>
                      </tr>
                      <tr>
                        <th>TIIE</th>
                        <td>{{ formatoMoneda(parametros.tiie, false) }}%</td>
                      </tr>
                      <tr>
                        <th>Tasa</th>
                        <td>{{ state.tasa }}%</td>
                      </tr>
                      <tr>
                        <th>Spreed</th>
                        <td>{{ spreed }}%</td>
                      </tr>
                    </table>
                    <table class="table table-bordered table-sm mt-4">
                      <tr class="bg-light text-dark">
                        <th colspan="3">Comisión</th>
                      </tr>
                      <tr>
                        <th>Ejecutivo</th>
                        <td>
                          {{
                            formatoMoneda(parametros.comision_ejecutivo, false)
                          }}%
                        </td>
                        <td>${{ formatoMoneda(comisionEjecutivo, false) }}</td>
                      </tr>
                      <tr>
                        <th>Lider</th>
                        <td>
                          {{ formatoMoneda(parametros.comision_lider, false) }}%
                        </td>
                        <td>${{ formatoMoneda(comisionLider, false) }}</td>
                      </tr>
                      <tr>
                        <th colspan="2"></th>
                        <th>
                          ${{
                            formatoMoneda(
                              comisionEjecutivo + comisionLider,
                              false,
                            )
                          }}
                        </th>
                      </tr>
                      <tr>
                        <th>Broker</th>
                        <td>
                          <div class="input-group">
                            <input
                              class="form-control form-control-sm"
                              type="tel"
                              id="comisionExterno"
                              v-model="state.comisionExterno"
                              :disabled="existenPlazosGuardados"
                              autocomplete="off"
                              required
                            />
                            <span class="input-group-text"> % </span>
                          </div>
                        </td>
                        <td>${{ formatoMoneda(comisionBroker, false) }}</td>
                      </tr>
                      <tr>
                        <th>Marketing</th>
                        <td>
                          {{
                            formatoMoneda(parametros.comision_marketing, false)
                          }}%
                        </td>
                        <td>${{ formatoMoneda(comisionMarketing, false) }}</td>
                      </tr>
                      <tr>
                        <th>Agencia</th>
                        <td>
                          <div class="input-group">
                            <input
                              class="form-control form-control-sm"
                              type="tel"
                              id="comisionAgencia"
                              v-model="state.comisionAgencia"
                              autocomplete="off"
                              :disabled="existenPlazosGuardados"
                              required
                            />
                            <span class="input-group-text"> % </span>
                          </div>
                        </td>
                        <td>${{ formatoMoneda(comisionAgencia, false) }}</td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.form-control {
  text-align: center !important;
}

.form-control:focus {
  border-radius: 0px !important;
}

table {
  font-size: 13px;
}

#tablaTotales {
  text-align: center !important;
}

.bg-light {
  text-align: center !important;
}

.input-group-text {
  font-size: 0.675rem !important;
}

table th,
table td {
  vertical-align: middle;
}
</style>
